import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogOut, CreditCard, Film } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { scrollToId } from "@/components/site/SmoothScroll";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const UserMenu = ({ user, logout }) => {
  const navigate = useNavigate();
  return (
  <DropdownMenu>
    <DropdownMenuTrigger className="flex items-center gap-2 rounded-full border border-[#e7e7ea] py-1 pl-1 pr-3 text-sm font-medium transition-colors hover:bg-[#F5F5F7]" data-testid="user-menu-trigger">
      {user.picture ? (
        <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        <span className="grid h-7 w-7 place-items-center rounded-full bg-[#0066FF] text-xs text-white">{user.name?.[0] || "M"}</span>
      )}
      {user.name?.split(" ")[0] || "Account"}
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56 rounded-xl">
      <DropdownMenuLabel className="truncate text-xs font-normal text-[#52525b]">{user.email}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer" data-testid="dashboard-menu-link">
        <Film className="mr-2 h-4 w-4" /> I miei video
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/abbonamento")} className="cursor-pointer" data-testid="account-menu-link">
        <CreditCard className="mr-2 h-4 w-4" /> Il mio abbonamento
      </DropdownMenuItem>
      <DropdownMenuItem onClick={logout} className="cursor-pointer" data-testid="logout-button">
        <LogOut className="mr-2 h-4 w-4" /> Esci
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (pathname === "/home") scrollToId(id);
    else navigate(`/home#${id}`);
  };

  const links = [
    { id: "tool", label: "Tool" },
    { id: "prezzi", label: "Prezzi" },
  ];

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled || open ? "border-b border-[#e7e7ea] bg-white/80 backdrop-blur-xl" : "border-b border-transparent"}`} data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/home" aria-label="MIMIS home" data-testid="header-logo-link">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="nav-link text-[0.95rem]" data-testid={`nav-${l.id}-link`}>{l.label}</button>
          ))}
          {user ? (
            <UserMenu user={user} logout={logout} />
          ) : (
            <button onClick={() => login()} className="nav-link text-[0.95rem]" data-testid="nav-login-button">Accedi</button>
          )}
          <Link to="/tool/mimis-video" className="btn-blu h-10 px-5 text-sm" data-testid="header-try-button">Prova MIMIS Video</Link>
        </nav>
        <button className="grid h-11 w-11 place-items-center rounded-full border border-[#e7e7ea] bg-white md:hidden" onClick={() => setOpen((v) => !v)} aria-label="Apri menu" data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden md:hidden" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 px-5 pb-6">
              {links.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} className="font-display py-3 text-left text-2xl font-semibold" data-testid={`mobile-nav-${l.id}-link`}>{l.label}</button>
              ))}
              {user ? (
                <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="font-display py-3 text-left text-2xl font-semibold" data-testid="mobile-dashboard-link">I miei video</Link>
                <Link to="/abbonamento" onClick={() => setOpen(false)} className="font-display py-3 text-left text-2xl font-semibold" data-testid="mobile-account-link">Il mio abbonamento</Link>
                <button onClick={() => { setOpen(false); logout(); }} className="font-display py-3 text-left text-2xl font-semibold" data-testid="mobile-logout-button">Esci ({user.name?.split(" ")[0]})</button>
                </>
              ) : (
                <button onClick={() => login()} className="font-display py-3 text-left text-2xl font-semibold" data-testid="mobile-login-button">Accedi</button>
              )}
              <Link to="/tool/mimis-video" onClick={() => setOpen(false)} className="btn-blu mt-3 h-12 text-base" data-testid="mobile-try-button">Prova MIMIS Video - Gratis</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
